import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Перетаскивание окна за handle. Позиция — абсолютные координаты (left/top).
 */
const useDraggable = ({ enabled = true, boundsRef } = {}) => {
  const nodeRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const placedRef = useRef(false);

  const clampToBounds = useCallback(
    (x, y, width, height) => {
      const boundsEl = boundsRef?.current;
      const maxX = (boundsEl?.clientWidth ?? window.innerWidth) - width;
      const maxY = (boundsEl?.clientHeight ?? window.innerHeight) - height;

      return {
        x: Math.min(Math.max(0, x), Math.max(0, maxX)),
        y: Math.min(Math.max(0, y), Math.max(0, maxY)),
      };
    },
    [boundsRef]
  );

  const centerWindow = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;

    const { width, height } = node.getBoundingClientRect();
    const boundsEl = boundsRef?.current;
    const areaW = boundsEl?.clientWidth ?? window.innerWidth;
    const areaH = boundsEl?.clientHeight ?? window.innerHeight;

    setPosition(
      clampToBounds((areaW - width) / 2, Math.max(24, (areaH - height) / 5), width, height)
    );
    placedRef.current = true;
  }, [boundsRef, clampToBounds]);

  useEffect(() => {
    if (!enabled) return undefined;

    centerWindow();

    const onResize = () => {
      const node = nodeRef.current;
      if (!node || !placedRef.current) return;

      const { width, height } = node.getBoundingClientRect();
      setPosition((prev) => clampToBounds(prev.x, prev.y, width, height));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, centerWindow, clampToBounds]);

  const onHandlePointerDown = useCallback(
    (event) => {
      if (!enabled || event.button !== 0) return;
      if (event.target.closest('button')) return;

      const node = nodeRef.current;
      if (!node) return;

      event.preventDefault();
      node.setPointerCapture?.(event.pointerId);

      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: position.x,
        originY: position.y,
      };
      setIsDragging(true);
    },
    [enabled, position.x, position.y]
  );

  useEffect(() => {
    if (!isDragging) return undefined;

    const onMove = (event) => {
      const drag = dragRef.current;
      const node = nodeRef.current;
      if (!drag || !node || event.pointerId !== drag.pointerId) return;

      const { width, height } = node.getBoundingClientRect();
      const nextX = drag.originX + (event.clientX - drag.startX);
      const nextY = drag.originY + (event.clientY - drag.startY);
      setPosition(clampToBounds(nextX, nextY, width, height));
    };

    const onUp = (event) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;

      dragRef.current = null;
      setIsDragging(false);
      nodeRef.current?.releasePointerCapture?.(event.pointerId);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isDragging, clampToBounds]);

  return {
    nodeRef,
    position,
    isDragging,
    onHandlePointerDown,
  };
};

export default useDraggable;
